
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Box, Theme, Tooltip, useTheme } from "@mui/material";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { DateFilterOperator, NumberFilterOperator, TaskProgressFilterOperator, TextFilterOperator, TaskStatusFilterOperator } from "components/shared/filters";
import { TaskProgressPill, TaskStatusCell } from "components/tasks";
import { TaskFilter } from "models/tasks";
import { SetPreferenceSignature } from "hooks/preferences";
import { useWorkItemPermissions } from "hooks/work-items";

type UseWorkItemTasksColumnProps = {
    status: TaskFilter,
    showUser?: boolean,
    theme?: Theme,
    settings?: IDashboardSettings | null,
    setSettings?: SetPreferenceSignature,
};

type UseWorkItemTasksColumnResult = [
    GridColDef[],
    GridColumnVisibilityModel,
    ColumnVisibilityOrOrderChangeHandler,
];

type UseWorkItemTasksGridColumnsSignature = (props: UseWorkItemTasksColumnProps) => UseWorkItemTasksColumnResult;

const useWorkItemTasksGridColumns: UseWorkItemTasksGridColumnsSignature = (props) => {

    const navigate = useNavigate();
    const navigateToTask = (taskId: number) => {
        navigate(`/tasks/${taskId}/overview`);
      };
    const permissions = useWorkItemPermissions();

    const ColumnsBase: GridColDef[] = [
        {
            field: 'name', headerName: 'Task',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true,
            minWidth: 250,
            filterOperators: TextFilterOperator,
            flex: 1,
            //* Temporarily commenting this until we have the definitive redirection to a task
            renderCell: (params) => {
                const name = params.row.name;
                const taskId = params.row.id;
                const hasAccess = params.row.userHasAccess;
                return (
                    <div style={{ whiteSpace: 'pre-wrap', flexWrap: 'nowrap' }}>
                        {hasAccess && 
                        <a onClick={() => navigateToTask(taskId)} style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }} rel="noopener noreferrer">
                            {name}
                        </a>
                        || name}
                    </div>
                )
            }
        },        
        {
            field: 'progress', headerName: 'Progress',
            headerClassName: 'header',
            width: 200, sortable: true, resizable: true,
            hideSortIcons: false,
            filterOperators: TaskProgressFilterOperator(true),
            renderCell: (params) => {                
                return (<TaskProgressPill progress={params.value} />)
            }
        },
        {
            field: 'endDate', headerName: 'End Date',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true, width: 200,
            filterOperators: DateFilterOperator,
            valueGetter: value => !value ? null : moment.utc(value).toDate(),
            renderCell: (params) => {
                const progress = params.row.progress;
                if (progress !== 'on-schedule') {
                    return (
                        <Box sx={{color:'#EB0000'}}>{formatDate(params.value)}</Box>
                        )
                } else {
                    return (
                        <div>{formatDate(params.value)}</div>
                        )
                }
            }
        },      
        {
            field: 'resource', headerName: 'Resource',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true, width: 200,
            filterOperators: TextFilterOperator,
        },
        {
            field: 'workflowName', headerName: 'Workflow',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true,
            minWidth: 200,
            filterOperators: TextFilterOperator,
            renderCell: (params) => {
                const name = params.value;
                const workflowId = params.row.workflowId;
                return (
                    <div style={{ whiteSpace: 'pre-wrap', flexWrap: 'nowrap' }}>
                        {
                            permissions?.canEditWorkflow ?
                                <a rel="noopener noreferrer" href={process.env.REACT_APP_CLASSIC_URL + `/workflowinstance/editworkflow?workflowInstanceId=${workflowId}`}
                                    style={{ color: '#0066CC', cursor: 'pointer' }}>
                                    {name}
                                </a>
                                :
                                <>{name}</>
                        }
                    </div>
                )
            }
        }
    ];

    const { status } = props;
    const { settings, setSettings } = props;
    const localTheme = useTheme();
    const theme = props?.theme ?? localTheme;
    const showUser = props?.showUser ?? false;
    const [columns, setColumns] = useState(ColumnsBase);

    const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

    useEffect(() => {
        let newColumns: GridColDef[] = ColumnsBase;

        if (status !== 'in-progress' && status !== 'all') {
            const progressIdx = newColumns.findIndex(c => c.field === 'progress');
            if (progressIdx > -1) newColumns.splice(progressIdx, 1);
        }

        if (status !== 'all') {
            const statusIdx = newColumns.findIndex(c => c.field === 'status');
            if (statusIdx > -1) newColumns.splice(statusIdx, 1);
        }

        if (showUser === false) {
            const userIdx = newColumns.findIndex(c => c.field === 'resource');
            if (userIdx > -1) newColumns.splice(userIdx, 1);
        }

        setColumns(newColumns);
    }, [props.showUser, props.status]);

    return [
        columnsModel,
        visibilityModel,
        handleColumnVisibilityOrOrderChange
    ];
};

export default useWorkItemTasksGridColumns;